import { SelectionModel } from "@angular/cdk/collections";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { Router } from "@angular/router";
import { BILLING_TYPE, DataType, IChargeItemsInfo, IFilter } from "@comunicare/comunicare-interfaces";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PdfTypeChoiceComponent, PDF_TYPE_CONTEXT } from "src/app/components/patient-QR/pdf-type-choice/pdf-type-choice.component";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { BillingDataSource } from "../../billing.datasource";
import { InvoiceNotesDialogComponent } from "../invoice-notes-dialog/invoice-notes-dialog.component";

@Component({
  selector: "app-invoice-table",
  templateUrl: "./invoice-table.component.html",
  styleUrls: ["./invoice-table.component.scss", "../../../../../assets/comunicare-table.scss"],
})
export class InvoiceTableComponent implements AfterViewInit {
  @Input() dataSource: BillingDataSource;
  /** Raw active filters, provided by the parent to feed `app-item-filter` via the `filterFor` pipe. */
  @Input() activeFilters: IFilter[] = [];
  @Input() data: IChargeItemsInfo[];
  @Input() availableColumnItems: Item[];

  @ViewChild(MatSort) sort: MatSort;
  @Output() sortChanged = new EventEmitter<Sort>();

  @Output() filterChanged = new EventEmitter<IFilter>();

  @ViewChild(MatTable) table: MatTable<unknown>;
  @ViewChild("masterCheckbox") masterCheckbox: MatCheckbox;

  @Output() send = new EventEmitter<string>();
  @Output() updateStatus = new EventEmitter<{ identifiers: string[]; newStatus: BILLING_TYPE }>();
  @Output() reload = new EventEmitter<void>();
  @Output() changeColumnOrder = new EventEmitter<Item[]>();

  protected onDestroy$ = new Subject<void>();

  public actionOnMultiple: boolean;
  public selection = new SelectionModel<IChargeItemsInfo>(true, []);
  public allSelected = false;

  public BILLING_TYPE: BILLING_TYPE[] = Object.keys(BILLING_TYPE).map((key) => key as BILLING_TYPE);

  displayedColumns: string[] = [
    "lastname",
    "firstname",
    "ssin",
    "birthDate",
    "category",
    "startDate",
    "endDate",
    "code",
    "totalPrice",
    "adherence",
    "status",
    "careplan",
    "prescriptionStart",
    "prescriptionDuration",
    "prescriptionStatus",
    "billingCycle",
    "primoPrescriptionStart",
    "internalId",
    "pseudoname",
    "actions",
  ];

  public dataType = DataType;

  billingStatusClasses: { [key in BILLING_TYPE]: string } = {
    [BILLING_TYPE.PLANNED]: "planned",
    [BILLING_TYPE.BILLABLE]: "billable",
    [BILLING_TYPE.ACCUMULATED]: "accumulated",
    [BILLING_TYPE.NOT_BILLABLE]: "not-billable",
    [BILLING_TYPE.BILLED]: "billed",
  };
  filters: IFilter[];

  constructor(public responsiveDialog: ResponsiveDialogService, private router: Router) {}

  ngAfterViewInit(): void {
    // Watch sort
    this.sort.sortChange.subscribe((value) => {
      this.sortChanged.next(value);
    });

    // Watch selection
    this.selection.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      const selectedCount = this.selection.selected.length;
      const totalCount = this.data.length;
      this.allSelected = selectedCount === totalCount;
      this.masterCheckbox.checked = this.allSelected;
      this.masterCheckbox.indeterminate = selectedCount > 0 && !this.allSelected;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public applyFilter(filter: IFilter): void {
    this.filterChanged.emit(filter);
  }

  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }
  public activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
    if (this.actionOnMultiple) {
      // this.dataSource.clearAutoRefresh();
      this.displayedColumns.unshift("select");
    } else {
      this.resetSelection();
    }
  }

  public resetSelection(): void {
    this.selection.clear();
    this.actionOnMultiple = false;
    if (this.displayedColumns[0] === "select") {
      this.displayedColumns.shift();
    }
  }

  public masterToggle(): void {
    if (this.selection.hasValue()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.data);
    }
  }

  public setDisplayedColumns(availableColumnItems: Item[]): void {
    this.displayedColumns = this.actionOnMultiple
      ? ["select", ...availableColumnItems.filter((item) => item.checked).map((item) => item.value)]
      : [...availableColumnItems.filter((item) => item.checked).map((item) => item.value)];
  }

  public download(chargeItem: IChargeItemsInfo): void {
    this.responsiveDialog.open(
      PdfTypeChoiceComponent,
      {
        data: {
          context: PDF_TYPE_CONTEXT.CHARGE_ITEM,
          chargeItemIds: [chargeItem.identifier[0].value],
          columns: this.getColumnNames(),
          isAggregatedView: false,
        },
        disableClose: true,
      },
      {},
      {
        minWidth: "auto",
        width: "auto",
        maxWidth: "auto",

        minHeight: "auto",
        height: "auto",
        maxHeight: "auto",
      }
    );
  }

  public onSend(chargeItem: IChargeItemsInfo): void {
    this.send.emit(chargeItem.identifier[0].value);
  }

  public onUpdateStatus(chargeItem: IChargeItemsInfo, newStatus: BILLING_TYPE): void {
    this.updateStatus.emit({ identifiers: [chargeItem.identifier[0].value], newStatus });
  }

  public showNotes(chargeItem: IChargeItemsInfo): void {
    const dialogRef = this.responsiveDialog.open(InvoiceNotesDialogComponent, {
      data: { chargeItem },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.reload.emit();
    });
  }

  public redirectToPatientPage(caremateIdentifier: string): void {
    const b64Id = Base64Helper.utf8_to_b64(caremateIdentifier);
    this.router.navigateByUrl(`/patient;id=${b64Id}`, {
      state: undefined,
    });
  }
  /**
   * Drag n'Drop
   */
  public async columnDropped(event: CdkDragDrop<string[]>): Promise<void> {
    const realIndexFrom =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.previousIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    const realIndexTo =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.currentIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.setDisplayedColumns(this.availableColumnItems);
    this.changeColumnOrder.emit(this.availableColumnItems);
  }

  private getColumnNames(): string[] {
    const names = [];
    this.displayedColumns.forEach((column) => {
      switch (column) {
        case "category":
          names.push("prescription.category");
          break;
        case "prescriptionStart":
          names.push("prescription.startDate");
          break;
        case "prescriptionDuration":
          names.push("prescription.duration");
          break;
        case "prescriptionStatus":
          names.push("prescription.status");
          break;
        case "billingCycle":
          names.push("prescription.pricingPeriodicity");
          break;
        case "primoPrescriptionStart":
          names.push("prescription.primoPrescriptionStart");
          break;
        case "careplan":
          names.push("officialCareplan");
          break;
        default:
          if (column !== "actions") {
            names.push(column);
          }
          break;
      }
    });
    return names;
  }
}
